import { useCallback, useEffect, useRef, useState } from "react";
import { wsOrigin } from "../../constants/api";
import { debug } from "../../utils/logging";
import useAuth from "../auth/useAuth";

export default function useWebSocket({
  path,
  enabled,
  messageTypes,
  onOpen,
  onClose,
  onMessage,
  onError,
  autoReconnect = true,
  reconnectInterval = 5000,
  ping = false,
  pingInterval = 30000,
}) {
  const { token } = useAuth();
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const messageTypesRef = useRef(messageTypes);

  // Tracks the underlying WebSocket connection state
  const [isOpen, setIsOpen] = useState(false);
  // Tracks the server readiness. This changes only after receiving a "ready" message.
  const [isReady, setIsReady] = useState(false);

  const connectSocket = useCallback(() => {
    // Note: the token is no longer sent via the URL
    const socketUrl = `${wsOrigin + path}`;
    socketRef.current = new WebSocket(socketUrl);

    socketRef.current.onopen = () => {
      setIsOpen(true);
      debug(`Connection to ${socketUrl} established`);

      // Send auth token as the first message for safer authentication
      if (token && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "auth", token }));
      }

      if (onOpen) {
        onOpen();
      }

      if (ping) {
        pingIntervalRef.current = window.setInterval(() => {
          if (
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN
          ) {
            socketRef.current.send(JSON.stringify({ type: "ping" }));
          }
        }, pingInterval);
      }
    };

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // If the server sends a "ready" message, mark the connection as fully ready.
      if (data.type === "ready") {
        setIsReady(true);
        return;
      }

      // Check if the message type is valid according to the allowed receive types.
      const validReceiveTypes =
        (messageTypesRef.current && messageTypesRef.current.receive) || {};
      if (!Object.values(validReceiveTypes).includes(data.type)) {
        throw new Error(
          `Invalid message type received for path '${path}.': ${data.type}`
        );
      }

      if (onMessage) {
        onMessage(data);
      }
    };

    socketRef.current.onerror = (event) => {
      if (onError) {
        onError(event);
      }
    };

    socketRef.current.onclose = (event) => {
      setIsOpen(false);
      setIsReady(false);
      debug(`Connection to ${socketUrl} closed`, event);
      if (onClose) {
        onClose(event);
      }

      if (pingIntervalRef.current !== null) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Attempt to reconnect if autoReconnect is enabled and the close wasn't normal (code 1000)
      if (autoReconnect && event.code !== 1000) {
        setTimeout(() => connectSocket(), reconnectInterval);
      }
    };
  }, [
    autoReconnect,
    onOpen,
    onClose,
    onMessage,
    onError,
    reconnectInterval,
    path,
    token,
    ping,
    pingInterval,
  ]);

  useEffect(() => {
    if (token && enabled) connectSocket();

    return () => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.close();
      }
      setIsOpen(false);
      setIsReady(false);
      if (pingIntervalRef.current !== null) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [token, path, connectSocket, enabled]);

  const send = (data) => {
    const validSendTypes =
      (messageTypesRef.current && messageTypesRef.current.send) || {};
    if (!Object.values(validSendTypes).includes(data.type)) {
      throw new Error(
        `Unable to send message of type '${data.type}': Invalid message type for path ${path}.`
      );
    } else if (
      socketRef.current &&
      socketRef.current.readyState === WebSocket.OPEN &&
      isReady
    ) {
      socketRef.current.send(JSON.stringify(data));
    } else {
      console.warn(
        `Unable to send message of type '${data.type}': WebSocket connection not established.`
      );
    }
  };

  return { socketRef, send, isOpen, isReady };
}
